import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SaveIcon, ArrowLeftIcon, EyeIcon } from 'lucide-react';
import { useProjects } from '../../lib/store';
import { Project } from '../../lib/types';
import { Button } from '../../components/ui/Button';
import { Tabs } from '../../components/ui/Tabs';
import { ContentTab } from './components/ContentTab';
import { BuilderTab } from './components/BuilderTab';
import { DesignTab } from './components/DesignTab';
import { SEOTab } from './components/SEOTab';
const emptyProject: Project = {
  id: '',
  title: '',
  num: '',
  year: new Date().getFullYear().toString(),
  subtitle: '',
  summary: '',
  role: '',
  tools: [],
  category: '',
  overview: '',
  challenge: '',
  approach: '',
  outcome: '',
  metrics: [],
  lessonsLearned: '',
  nextSteps: '',
  ctaText: 'View Live Project',
  ctaLink: '',
  liveUrl: '',
  prototypeUrl: '',
  slug: '',
  status: 'draft',
  featured: false,
  seoTitle: '',
  seoDescription: '',
  ogImage: '',
  coverImage: '',
  heroStyle: 'default',
  accentColor: 'hsl(72 100% 50%)',
  backgroundColor: 'hsl(0 0% 6%)',
  textColor: 'hsl(0 0% 92%)',
  sectionSpacing: 'medium',
  customCSS: '',
  projectImages: [],
  blocks: [],
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString()
};
export function ProjectEditor() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const {
    getProject,
    saveProject
  } = useProjects();
  const [project, setProject] = useState<Project>(emptyProject);
  const [isSaving, setIsSaving] = useState(false);
  useEffect(() => {
    if (id && id !== 'new') {
      const existing = getProject(id);
      if (existing) {
        setProject(existing);
      }
    } else {
      setProject({
        ...emptyProject,
        id: Math.random().toString(36).substr(2, 9)
      });
    }
  }, [id, getProject]);
  const handleSave = () => {
    setIsSaving(true);
    const updatedProject = {
      ...project,
      updatedAt: new Date().toISOString()
    };
    saveProject(updatedProject);
    setTimeout(() => {
      setIsSaving(false);
      if (id === 'new') {
        navigate(`/admin/work/${updatedProject.id}`, {
          replace: true
        });
      }
    }, 500);
  };
  // ISSUE 3 FIX: Save before previewing so draft/unsaved content is visible
  const handlePreview = () => {
    const updatedProject = {
      ...project,
      updatedAt: new Date().toISOString()
    };
    saveProject(updatedProject);
    // Open the project detail page which renders page builder blocks
    window.open(`/project/${project.slug || project.id}`, '_blank');
  };
  const updateField = (field: keyof Project, value: any) => {
    setProject(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const tabs = [{
    id: 'content',
    label: 'Content',
    content: <ContentTab project={project} updateField={updateField} data-id="element-1494" />
  }, {
    id: 'builder',
    label: 'Page Builder',
    content: <BuilderTab project={project} updateField={updateField} data-id="element-1495" />
  }, {
    id: 'design',
    label: 'Design Settings',
    content: <DesignTab project={project} updateField={updateField} data-id="element-1496" />
  }, {
    id: 'seo',
    label: 'SEO & Meta',
    content: <SEOTab project={project} updateField={updateField} data-id="element-1497" />
  }];
  return <div className="flex flex-col h-full bg-background" data-id="element-1498">
      {/* Header */}
      <header className="flex items-center justify-between px-8 py-4 border-b border-border bg-card sticky top-0 z-10" data-id="element-1499">
        <div className="flex items-center gap-4" data-id="element-1500">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/work')} className="px-2" data-id="element-1501">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-1502" />
          </Button>
          <div data-id="element-1503">
            <h1 className="font-display text-xl font-bold tracking-tight" data-id="element-1504">
              {project.title || 'Untitled Project'}
            </h1>
            <div className="flex items-center gap-2 mt-1" data-id="element-1505">
              <span className={`inline-flex items-center px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest border ${project.status === 'published' ? 'border-primary/50 text-primary bg-primary/10' : 'border-border text-muted-foreground bg-secondary'}`} data-id="element-1506">
                {project.status}
              </span>
              <span className="font-mono text-xs text-muted-foreground" data-id="element-1507">
                {project.slug ? `/${project.slug}` : 'No slug set'}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3" data-id="element-1508">
          <select value={project.status} onChange={e => updateField('status', e.target.value)} className="h-10 px-3 py-2 bg-secondary border border-border font-mono text-xs uppercase tracking-widest focus:outline-none focus:border-primary" data-id="element-1509">
            <option value="draft" data-id="element-1510">Draft</option>
            <option value="published" data-id="element-1511">Published</option>
            <option value="archived" data-id="element-1512">Archived</option>
          </select>

          <Button variant="outline" className="gap-2" onClick={handlePreview} data-id="element-1513">
            <EyeIcon className="w-4 h-4" data-id="element-1514" />
            Preview
          </Button>

          <Button onClick={handleSave} disabled={isSaving} className="gap-2 min-w-[120px]" data-id="element-1515">
            <SaveIcon className="w-4 h-4" data-id="element-1516" />
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </header>

      {/* Main Editor Area */}
      <main className="flex-1 overflow-hidden p-8 max-w-7xl mx-auto w-full" data-id="element-1517">
        <Tabs tabs={tabs} data-id="element-1518" />
      </main>
    </div>;
}